import { parseGIF } from 'gifuct-js'

export type GifFrameMetadata = Readonly<{
  delayCentiseconds: number
  disposalMethod: number
  hasTransparency: boolean
}>

export type GifMetadata = Readonly<{
  width: number
  height: number
  frameCount: number
  decodedPixels: number
  loopCount: number
  frames: readonly GifFrameMetadata[]
}>

const GIF_TRAILER = 0x3b
const NO_LOOP_EXTENSION = -1

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isIntegerInRange = (
  value: unknown,
  minimum: number,
  maximum: number,
): value is number =>
  typeof value === 'number' &&
  Number.isInteger(value) &&
  value >= minimum &&
  value <= maximum

const isByteSequence = (value: unknown): value is Uint8Array | number[] =>
  value instanceof Uint8Array ||
  (Array.isArray(value) &&
    value.every((byte) => isIntegerInRange(byte, 0, 0xff)))

const hasGifHeader = (bytes: Uint8Array): boolean =>
  bytes[0] === 0x47 &&
  bytes[1] === 0x49 &&
  bytes[2] === 0x46 &&
  bytes[3] === 0x38 &&
  (bytes[4] === 0x37 || bytes[4] === 0x39) &&
  bytes[5] === 0x61

const throwMalformedGif = (): never => {
  throw new TypeError('GIF data is malformed')
}

const readLogicalDimensions = (
  parsed: Record<string, unknown>,
): Readonly<{ width: number; height: number }> => {
  if (!isRecord(parsed.header) || !isRecord(parsed.lsd)) {
    return throwMalformedGif()
  }

  if (
    parsed.header.signature !== 'GIF' ||
    (parsed.header.version !== '87a' && parsed.header.version !== '89a') ||
    !isIntegerInRange(parsed.lsd.width, 1, 0xffff) ||
    !isIntegerInRange(parsed.lsd.height, 1, 0xffff)
  ) {
    return throwMalformedGif()
  }

  return {
    width: parsed.lsd.width,
    height: parsed.lsd.height,
  }
}

const readFrameMetadata = (
  frame: Record<string, unknown>,
  canvasWidth: number,
  canvasHeight: number,
): GifFrameMetadata | null => {
  if (!isRecord(frame.image)) return null

  const descriptor = frame.image.descriptor
  const data = frame.image.data

  if (
    frame.image.code !== 0x2c ||
    !isRecord(descriptor) ||
    !isRecord(data) ||
    !isIntegerInRange(descriptor.left, 0, 0xffff) ||
    !isIntegerInRange(descriptor.top, 0, 0xffff) ||
    !isIntegerInRange(descriptor.width, 1, 0xffff) ||
    !isIntegerInRange(descriptor.height, 1, 0xffff) ||
    descriptor.left + descriptor.width > canvasWidth ||
    descriptor.top + descriptor.height > canvasHeight ||
    !isIntegerInRange(data.minCodeSize, 2, 8) ||
    !isByteSequence(data.blocks) ||
    data.blocks.length === 0
  ) {
    return throwMalformedGif()
  }

  if (frame.gce === undefined) {
    return {
      delayCentiseconds: 0,
      disposalMethod: 0,
      hasTransparency: false,
    }
  }

  if (
    !isRecord(frame.gce) ||
    !isRecord(frame.gce.extras) ||
    frame.gce.byteSize !== 4 ||
    frame.gce.terminator !== 0 ||
    !isIntegerInRange(frame.gce.delay, 0, 0xffff) ||
    !isIntegerInRange(frame.gce.transparentColorIndex, 0, 0xff) ||
    !isIntegerInRange(frame.gce.extras.disposal, 0, 7) ||
    typeof frame.gce.extras.transparentColorGiven !== 'boolean'
  ) {
    return throwMalformedGif()
  }

  return {
    delayCentiseconds: frame.gce.delay,
    disposalMethod: frame.gce.extras.disposal,
    hasTransparency: frame.gce.extras.transparentColorGiven,
  }
}

const readLoopCount = (frames: readonly unknown[]): number => {
  for (const frame of frames) {
    if (!isRecord(frame) || !isRecord(frame.application)) continue

    const { id, blocks } = frame.application
    if (id !== 'NETSCAPE2.0' && id !== 'ANIMEXTS1.0') continue

    if (
      !isByteSequence(blocks) ||
      blocks.length < 3 ||
      blocks[0] !== 1 ||
      blocks[1] === undefined ||
      blocks[2] === undefined
    ) {
      return throwMalformedGif()
    }

    return blocks[1] + (blocks[2] << 8)
  }

  return NO_LOOP_EXTENSION
}

export const parseGifMetadata = (bytes: Uint8Array): GifMetadata => {
  if (
    bytes.length < 14 ||
    !hasGifHeader(bytes) ||
    bytes[bytes.length - 1] !== GIF_TRAILER
  ) {
    return throwMalformedGif()
  }

  let parsed: unknown

  try {
    parsed = parseGIF(Uint8Array.from(bytes).buffer)
  } catch {
    return throwMalformedGif()
  }

  if (!isRecord(parsed) || !Array.isArray(parsed.frames)) {
    return throwMalformedGif()
  }

  const { width, height } = readLogicalDimensions(parsed)
  const frames: GifFrameMetadata[] = []

  for (const frame of parsed.frames) {
    if (!isRecord(frame)) return throwMalformedGif()

    const metadata = readFrameMetadata(frame, width, height)
    if (metadata) frames.push(metadata)
  }

  if (frames.length === 0) return throwMalformedGif()

  const decodedPixels = width * height * frames.length
  if (!Number.isSafeInteger(decodedPixels)) return throwMalformedGif()

  return {
    width,
    height,
    frameCount: frames.length,
    decodedPixels,
    loopCount: readLoopCount(parsed.frames),
    frames,
  }
}
